import clientLogo from "@assets/images/ara-icon.svg";
import avatarSpinner from "@assets/images/avatar_spinner.svg";
import { Typography, Box, List, ListItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import MarkdownRenderer from "../MarkdownRenderer";
import SourcesButton from "../SourcesButton";

export enum AvatarChatHistoryMessageSource {
  USER = "user",
  AVATAR = "avatar"
}

export interface IAvatarChatHistoryMessage {
  source: AvatarChatHistoryMessageSource;
  text: string;
  timestamp: Date;
  isComplete?: boolean;
  sources?: string[]; // Array of source IDs from metadata event
}

export interface IAvatarChatHistoryProps {
  messages: IAvatarChatHistoryMessage[];
  noSidePadding?: boolean;
  onSourcesClick?: (sourceIds: string[]) => void;
}

const InteractiveChatHistory = ({
  messages,
  noSidePadding = false,
  onSourcesClick
}: IAvatarChatHistoryProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const chatMessages = messages.map((message, index) => {
    // Check if current message is from USER and next message is from AVATAR
    const isUserFollowedByBot =
      message.source === AvatarChatHistoryMessageSource.USER &&
      index < messages.length - 1 &&
      messages[index + 1].source === AvatarChatHistoryMessageSource.AVATAR;

    return (
      <ListItem
        key={index}
        sx={{
          justifyContent:
            message.source === AvatarChatHistoryMessageSource.USER
              ? "flex-end"
              : "flex-start",
          px: noSidePadding ? 0 : 3,
          py: 1,
          pb: isUserFollowedByBot ? "13px" : undefined, // Add 5px more (8px base + 5px = 13px)
          alignItems: "flex-start"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: {
              xs:
                message.source === AvatarChatHistoryMessageSource.AVATAR
                  ? 0.5
                  : 1,
              sm: 1
            },
            maxWidth:
              message.source === AvatarChatHistoryMessageSource.AVATAR
                ? "100%"
                : "90%",
            flexDirection: {
              xs:
                message.source === AvatarChatHistoryMessageSource.USER
                  ? "column-reverse"
                  : "column",
              sm:
                message.source === AvatarChatHistoryMessageSource.USER
                  ? "row-reverse"
                  : "row"
            }
          }}
        >
          {message.source === AvatarChatHistoryMessageSource.AVATAR ? (
            <img
              src={
                message.text === t("chatbot.actions.thinking") ||
                message.text === ""
                  ? avatarSpinner
                  : clientLogo
              }
              alt="Avatar"
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                marginTop: 0,
                animation:
                  message.text === t("chatbot.actions.thinking") ||
                  message.text === ""
                    ? "spin 1s linear infinite"
                    : "none"
              }}
            />
          ) : null}

          <Box
            sx={{
              bgcolor:
                message.source === AvatarChatHistoryMessageSource.AVATAR
                  ? theme.palette.background.paper
                  : theme.palette.customColors.chipBg,
              color: theme.palette.text.primary,
              p:
                message.source === AvatarChatHistoryMessageSource.AVATAR
                  ? {
                      xs: "12px 0px 16px 0px", // 0px left padding in mobile
                      sm: "12px 16px 16px 16px"
                    }
                  : 2,
              borderRadius: 2,
              boxShadow: "none",
              border: "none",
              position: "relative",
              maxWidth: "100%",
              marginTop:
                message.source === AvatarChatHistoryMessageSource.AVATAR ? 0 : 0
            }}
          >
            {message.source === AvatarChatHistoryMessageSource.AVATAR ? (
              <Box>
                {message.text === t("chatbot.actions.thinking") ||
                message.text === "" ? (
                  <Typography
                    sx={{
                      fontFamily: "Ringside Narrow",
                      fontWeight: {
                        xs: 325,
                        sm: 325
                      },
                      fontSize: {
                        xs: "16px",
                        sm: 15
                      },
                      lineHeight: {
                        xs: "24px",
                        sm: "normal"
                      },
                      letterSpacing: {
                        xs: "0%",
                        sm: "normal"
                      },
                      color: theme.palette.customColors.textPrimary
                    }}
                  >
                    {t("chatbot.actions.thinking")}
                  </Typography>
                ) : (
                  <>
                    <MarkdownRenderer content={message.text} isUser={false} />
                    {/* Sources buttons - show if message has sources (complete or from history) */}
                    {message.sources && message.sources.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5
                        }}
                      >
                        <SourcesButton
                          sourceIds={message.sources}
                          onClick={() => {
                            onSourcesClick?.(message.sources || []);
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            ) : (
              <Typography
                variant="body1"
                sx={{
                  textAlign: "left",
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: {
                    xs: 325, // Book
                    sm: 325
                  },
                  fontSize: {
                    xs: "16px", // 16px in mobile
                    sm: 15
                  },
                  lineHeight: {
                    xs: "24px", // 24px in mobile
                    sm: "normal"
                  },
                  letterSpacing: {
                    xs: "0%", // 0% in mobile
                    sm: "normal"
                  },
                  overflowWrap: "break-word",
                  color: theme.palette.customColors.textPrimary
                }}
              >
                {message.text}
              </Typography>
            )}
          </Box>
        </Box>
      </ListItem>
    );
  });

  return (
    <>
      <List
        sx={{
          flex: 1,
          overflow: "auto",
          bgcolor: theme.palette.background.paper,
          py: 1,
          pb: noSidePadding ? 0 : undefined,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent"
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "primary.main",
            borderRadius: 3,
            "&:hover": {
              bgcolor: "primary.dark"
            }
          }
        }}
      >
        {chatMessages}
      </List>
    </>
  );
};

export default InteractiveChatHistory;
